import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    EfeAccordionValues,
    EfeResponse,
    Field,
    RealOwnerAdditionalInformation,
} from 'commons/models';
import {Constants, HistoryRoutes, Messages, SnackbarVariants, ViewKeys} from 'commons/constants';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {useFlowFieldSearch, useSpinner} from 'hooks';
import {nextStep} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import RepresentativeAdditionalForm from './representative-additional-form/RepresentativeAdditionalForm';
import {ClientService} from 'services';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';

function RepresentativeAdditionalInfo() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const {setTempRepresentative, selectedRepresentative, tempRepresentative} =
        useRelatedPeopleContext();
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [originalFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const {menu, kycId, fatherKycId} = useMenuContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {enqueueSnackbar} = useSnackbar();
    const [auxLegalRepresentative, setAuxLegalRepresentative] = React.useState<any>(undefined);

    React.useEffect(() => {
        if (
            fatherKycId > 0 &&
            kycId > 0 &&
            selectedRepresentative > 0 &&
            ViewKeys.LEGAL_ADDITIONAL_INFO === flow.data.expandKey
        ) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<RealOwnerAdditionalInformation> =
                        await ClientService.getRealOwnerAdditionalInformation(kycId);
                    if (response && response.data) {
                        setAuxLegalRepresentative(response.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setAuxLegalRepresentative(undefined);
                }
            })();
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Datos Adicionales',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <RepresentativeAdditionalForm
                            fields={fields}
                            submitHandler={onSubmit}
                            auxLegalRepresentative={auxLegalRepresentative}
                            filterTradedFields={filterTradedFields}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.LEGAL_ADDITIONAL_INFO === flow.data.expandKey,
        },
    ];

    async function onSubmit(values: RealOwnerAdditionalInformation) {
        const request = {...values};
        request.kycId = kycId;
        try {
            setIsLoading(true);
            const response: EfeResponse<RealOwnerAdditionalInformation> =
                await ClientService.saveRealOwnerAdditionalInformation(request);
            if (response && response.data) {
                setFields([...fields]);
                if (tempRepresentative) {
                    setTempRepresentative(undefined);
                }
                const path = nextStep(flow, menu);
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
                setFields([...fields]);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    function filterTradedFields(traded: boolean) {
        if (traded && originalFields && originalFields.length) {
            setFields([...originalFields]);
        } else {
            if (originalFields && originalFields.length) {
                const tempFields: Array<Field> = originalFields.filter(
                    (field: Field) =>
                        !['quotePersonName', 'participationPercentage', 'boardKey'].includes(
                            field.attrName
                        )
                );
                setFields([...tempFields]);
            }
        }
    }

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}

            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.REPRESENTATIVE_ADDITIONAL)}
                values={accordion}
            />
        </MainContent>
    );
}

export default RepresentativeAdditionalInfo;
